import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {Contact, CrmActivity, OrganizationCrmService, OrgOpportunity, UserLoggableActivityType} from '../../shared/organization-crm.service';

/**
 * CRM panel for a Client (contacts + activity timeline), keyed by the Client id.
 * Folded out of the old standalone /clients/prospects/:id ProspectComponent so
 * the unified Client detail can surface the same contacts/timeline. All CRM
 * endpoints are nested under the Client id and tenant-scoped server-side.
 */
@Component({
    selector: 'bst-client-crm-section',
    templateUrl: './client-crm-section.component.html',
    styleUrls: ['./client-crm-section.component.scss'],
    standalone: false,
})
export class ClientCrmSectionComponent implements OnChanges {

    @Input() clientId: number | null = null;

    public contacts: Contact[] = [];
    public newContact: Partial<Contact> = {};
    public editingContactId: number | null = null;
    public activities: CrmActivity[] = [];
    public newActivity: {type: UserLoggableActivityType; body: string; Contact_id?: number | null; occurred_at?: string} = {type: 'note', body: ''};
    public activityTypes: UserLoggableActivityType[] = ['note', 'call', 'email', 'meeting', 'cover_letter'];
    public opportunities: OrgOpportunity[] = [];

    constructor(private crm: OrganizationCrmService) {}

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes['clientId']) {
            await this.loadCrm();
        }
    }

    async loadCrm(): Promise<void> {
        if (!this.clientId) {
            this.contacts = [];
            this.activities = [];
            this.opportunities = [];
            return;
        }
        // Each list is independently non-fatal (errorify toasted), so one failed
        // call neither skips the others nor leaves the previous client's rows.
        this.contacts = await this.crm.listContacts(this.clientId).catch(() => []);
        await this.loadActivities();
        this.opportunities = await this.crm.listOpportunities(this.clientId).catch(() => []);
    }

    private async loadActivities(): Promise<void> {
        if (!this.clientId) { return; }
        this.activities = await this.crm.listActivities(this.clientId).catch(() => []);
    }

    async addContact(): Promise<void> {
        if (!this.clientId || !this.newContact.name) { return; }
        await this.crm.createContact(this.clientId, this.newContact);
        this.newContact = {};
        await this.loadCrm();
    }

    startEditContact(c: Contact): void {
        this.editingContactId = c.id;
    }

    async saveContact(c: Contact): Promise<void> {
        if (!this.clientId) { return; }
        await this.crm.updateContact(this.clientId, c.id, {
            name: c.name, role: c.role, email: c.email, phone: c.phone,
            is_primary: c.is_primary, notes: c.notes,
        });
        this.editingContactId = null;
        await this.loadCrm();
    }

    async makePrimary(c: Contact): Promise<void> {
        if (!this.clientId) { return; }
        await this.crm.updateContact(this.clientId, c.id, {is_primary: true});
        await this.loadCrm();
    }

    async removeContact(c: Contact): Promise<void> {
        if (!this.clientId) { return; }
        await this.crm.deleteContact(this.clientId, c.id);
        await this.loadCrm();
    }

    async logActivity(): Promise<void> {
        if (!this.clientId || !this.newActivity.body) { return; }
        await this.crm.logActivity(this.clientId, this.newActivity);
        this.newActivity = {type: 'note', body: ''};
        await this.loadActivities();
    }

    activityLabel(a: CrmActivity): string {
        return a.user ? `${a.user.name_first ?? ''} ${a.user.name_last ?? ''}`.trim() : '';
    }

    opportunityOwnerName(o: OrgOpportunity): string {
        return o.owner ? `${o.owner.name_first ?? ''} ${o.owner.name_last ?? ''}`.trim() : '';
    }
}
